import { CheckCircle2 } from "lucide-react";
import Seo, { breadcrumbSchema } from "@/components/Seo";
import { Reveal, CallButton } from "@/components/Blocks";
import { BUSINESS } from "@/data/site";
import { trackCall } from "@/lib/analytics";
import ServiceRequestForm from "@/components/ServiceRequestForm";

const NEXT_STEPS = [
    "We review your request and contact you to confirm availability and appointment details.",
    "Most appointments can be scheduled within about 24 hours, subject to availability.",
    "The $155 service call is waived with a completed repair, and completed repairs include a 6-month warranty.",
];

export default function RequestServicePage() {
    return (
        <div>
            <Seo
                title="Request Appliance Repair Service | Appliance Mania | Brevard & Seminole FL"
                description="Request in-home appliance repair in Brevard & Seminole County, FL. We review every request and confirm availability and appointment details. Prefer to talk? Call (321) 475-2230."
                path="/request-service"
                schemas={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Request Service", path: "/request-service" }])]}
            />
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-9 sm:py-14">
                <div className="grid lg:grid-cols-[1fr_340px] gap-10 items-start">
                    <div>
                        <p className="font-mono-brand text-xs font-bold uppercase tracking-[0.2em] text-red-600 mb-3">Request service</p>
                        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.1]">
                            Request Appliance Repair
                        </h1>
                        <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl">
                            Fill out the form below and we'll contact you to confirm availability and appointment details.
                            <strong> Submitting this form sends a service request — it does not reserve or guarantee an appointment time.</strong> Rather just talk to someone? Call us:
                        </p>
                        <div className="mt-5 mb-7">
                            <CallButton location="request-page-top" testid="request-page-call-btn" />
                        </div>
                        <ServiceRequestForm />
                    </div>
                    <aside className="space-y-5 lg:sticky lg:top-28">
                        <Reveal>
                            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="request-next-steps">
                                <h2 className="font-heading text-lg font-bold text-slate-900">What happens next</h2>
                                <ul className="mt-4 space-y-3">
                                    {NEXT_STEPS.map((s) => (
                                        <li key={s} className="flex items-start gap-2.5 text-sm text-slate-600 leading-relaxed">
                                            <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                                            <span>{s}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </Reveal>
                        <Reveal delay={0.08}>
                            <div className="rounded-2xl bg-slate-950 text-white p-6" data-testid="request-emergency-box">
                                <h2 className="font-heading text-lg font-bold">Urgent or after-hours?</h2>
                                <p className="mt-2 text-sm text-slate-200 leading-relaxed">
                                    After-hours emergency service may be available from 10 PM to 6 AM — call{" "}
                                    <a href={BUSINESS.tel} onClick={() => trackCall("request-page-after-hours")} className="text-amber-400 font-semibold">{BUSINESS.phone}</a>{" "}
                                    for availability. The form is not monitored overnight.
                                </p>
                            </div>
                        </Reveal>
                    </aside>
                </div>
            </section>
        </div>
    );
}
